import { create } from 'zustand';
import type { PatientProfile } from '@/services/domains/PatientDomain';
import type { MatchedDietitian, SessionResponse } from '@/components/self-referral/types';

export type DirectBookingStep = 'info' | 'confirmation';

interface DirectBookingState {
  step: DirectBookingStep;
  dietitian: MatchedDietitian | null;
  sessionData: SessionResponse | null;
  patientProfile: PatientProfile | null;
  prefilledConditions: string[];
  isLoading: boolean;
  isProcessing: boolean;
  error: string | null;

  setStep: (step: DirectBookingStep) => void;
  setDietitian: (dietitian: MatchedDietitian | null) => void;
  setSessionData: (session: SessionResponse | null) => void;
  setPatientProfile: (profile: PatientProfile | null) => void;
  setPrefilledConditions: (conditions: string[]) => void;
  setLoading: (value: boolean) => void;
  setProcessing: (value: boolean) => void;
  setError: (message: string | null) => void;
  reset: () => void;
}

const initialState: Omit<DirectBookingState, keyof Pick<DirectBookingState,
  'setStep' | 'setDietitian' | 'setSessionData' | 'setPatientProfile' |
  'setPrefilledConditions' | 'setLoading' | 'setProcessing' | 'setError' | 'reset'
>> = {
  step: 'info',
  dietitian: null,
  sessionData: null,
  patientProfile: null,
  prefilledConditions: [],
  isLoading: true,
  isProcessing: false,
  error: null,
};

export const useDirectBookingStore = create<DirectBookingState>((set) => ({
  ...initialState,

  setStep: (step) => set({ step }),
  setDietitian: (dietitian) => set({ dietitian }),
  setSessionData: (sessionData) => set({ sessionData }),
  setPatientProfile: (patientProfile) => set({ patientProfile }),
  setPrefilledConditions: (prefilledConditions) => set({ prefilledConditions }),
  setLoading: (isLoading) => set({ isLoading }),
  setProcessing: (isProcessing) => set({ isProcessing }),
  setError: (error) => set({ error }),
  reset: () => set(initialState),
}));
